import React, { useCallback, useContext, useEffect, useRef } from 'react';
import type { IMenuItemProps } from '../../../Menu.types';
import { createEventListeners } from './utils';
import classes from './style/MenuItem.module.scss';
import {
  MENU_ITEM_ANIMATION_CONFIG,
  MenuParts,
  INITIAL_ANIMATION_STATE,
  ANIMATION_PACKAGE_CONFIG,
} from '../../constants';
import { MenuItemLabel } from './MenuItemLabel';
import { isEmptyObject } from '@wix/editor-elements-common-utils';
import { useAnimationState } from './useAnimationState';
import { useDropdown } from './useDropdown';
import { DropdownContext } from '../../DropdownContext';

export const MenuItem: React.FC<IMenuItemProps> = props => {
  const {
    item,
    currentItem,
    focusOnMenuItem,
    getAnimationPackage,
    partToPreviewStateMap,
    HorizontalDropdown,
    VerticalDropdown,
    submenuDirection,
    itemClassName,
    dropdownItemClassName,
    dropdownSubItemClassName,
    onItemClick,
    onItemMouseIn,
    onItemMouseOut,
    onItemDblClick,
  } = props;
  const { children, forceOpen = false } = item;

  const { animationState, initEnterAnimation, initExitAnimation } =
    useAnimationState(MENU_ITEM_ANIMATION_CONFIG, INITIAL_ANIMATION_STATE);

  const hasSubItems = !!item.items?.length;
  const hasMegaMenuContainer = !!children;
  const hasBuilderContainer = !!item.children;
  const hasContainer = hasMegaMenuContainer || hasBuilderContainer;
  const hasDropdownMenu = hasContainer || hasSubItems;
  const hasLink = !isEmptyObject(item.link);

  const chevronButtonRef = useRef<HTMLButtonElement>(null);

  const itemRef = useRef<HTMLLIElement>(null);

  const getMenuItemAnimationName = () => {
    const animationPackage = getAnimationPackage();
    const menuItemAnimationName =
      ANIMATION_PACKAGE_CONFIG[animationPackage].menuItemAnimationName;

    return menuItemAnimationName;
  };

  const {
    isOpen,
    dropdownAnimationState,
    showDropdown,
    hideDropdown,
    toggleDropdown,
    updateDropdownDomStyles,
  } = useDropdown({
    itemRef,
    forceOpen,
    getAnimationPackage,
  });

  const { setDropdownDomStylesApplier } = useContext(DropdownContext);
  // Only applies in Builder (DropdownContext is not provided in Studio implementation)
  setDropdownDomStylesApplier(updateDropdownDomStyles);

  const eventListeners = createEventListeners({
    showDropdown,
    hideDropdown,
    itemRef,
    getMenuItemAnimationName,
    initEnterAnimation,
    initExitAnimation,
    isOpen,
    hasLink,
    dropdownAnimationState,
  });

  useEffect(() => {
    if (currentItem?.id === item.id && itemRef.current) {
      focusOnMenuItem?.(itemRef.current);
    }
  }, [currentItem, item, focusOnMenuItem]);

  const handleEscKeyDown = useCallback(() => {
    chevronButtonRef.current?.focus();
    hideDropdown();
  }, [hideDropdown, chevronButtonRef]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleEscKeyDown();
      }
    },
    [handleEscKeyDown],
  );

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  const isElementInView = (element: HTMLLIElement) => {
    const boundingRect = element.getBoundingClientRect();
    return boundingRect.top >= 0 && boundingRect.bottom <= window.innerHeight;
  };

  const scrollIntoViewIfNeeded = () => {
    const element = itemRef.current;

    if (element && !isElementInView(element)) {
      element.scrollIntoView();
    }
  };

  const sharedDropdownProps = {
    item,
    currentItem,
    dropdownAnimationState,
    onEscKeyDown: handleEscKeyDown,
    itemClassName: dropdownItemClassName,
    subItemClassName: dropdownSubItemClassName,
    onItemClick,
    onItemMouseIn,
    onItemMouseOut,
    onItemDblClick,
  };

  return (
    <li
      ref={itemRef}
      className={classes.listItem}
      data-part={MenuParts.MenuItem}
      data-animation-name={animationState.name}
      data-animation-state={animationState.phase}
      data-item-depth="0" // For scrolling, to know how much items on depth=0
      onFocus={scrollIntoViewIfNeeded}
    >
      <div className={classes.itemWrapper} {...eventListeners}>
        <MenuItemLabel
          {...props}
          className={itemClassName}
          hasDropdownMenu={hasDropdownMenu}
          hasSubItems={hasSubItems}
          chevronButtonRef={chevronButtonRef}
          toggleDropdown={toggleDropdown}
          isOpen={isOpen}
          dropdownAnimationState={dropdownAnimationState}
        />
        {hasDropdownMenu && (
          <HorizontalDropdown
            {...sharedDropdownProps}
            children={children}
            direction={submenuDirection}
          />
        )}
      </div>
      {hasSubItems && (
        <VerticalDropdown
          {...sharedDropdownProps}
          isOpen={isOpen}
          partToPreviewStateMap={partToPreviewStateMap}
          direction={submenuDirection}
        />
      )}
      <span className={classes.divider}></span>
    </li>
  );
};
